import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { debounceTime, switchMap } from 'rxjs/operators';
import { AnalyticsService } from '../../@core/utils/analytics.service';
import { ACCELERATION_EVENT, HOLIDAY_EVENT, PUPIL_EVENT, UNASSIGNED_EVENT } from '../../config/events';
import { TEACHERS_LIST } from '../../config/teachers-list';
import { ROLE } from '../../enum/roles.enum';
import { SUBJECT_DISPLAY_NAMES } from '../../enum/subject-enum';
import { UserActivityStatus } from '../../enum/user-activity-status.enum';
import { CalendarDayEvent } from '../../interface/calendar';
import { UserData } from '../../interface/user';
import { AuthService } from '../../services/auth.service';
import { CalendarService } from '../../services/calendar.service';
import { NotificationsService } from '../../services/notifications.service';
import { SchoolService } from '../../services/school.service';
import { StorageService } from '../../services/storage.service';
import { SubjectService } from '../../services/subject.service';
import { UtilService } from './../../services/util.service';

@Component({
  selector: 'ngx-teachers-list',
  templateUrl: './teachers-list.component.html',
  styleUrls: ['./teachers-list.component.scss'],
})
export class TeachersListComponent implements OnInit {
  schools: any;
  searchText = '';
  teachers = [];
  allTeachers = [];
  currentSchoolId: number;
  user: UserData | any;
  @Input() isDashboard = false;
  @Input() isExpand = false;
  @Input() isMock: boolean;
  loading = false;
  currentPage = 1;
  totalPages = 1;
  pages = [];
  dates;
  currentCalendarYearId;
  searchControl = new FormControl();
  isPaginate: boolean = true;
  currentDate = new Date().getTime();
  currentCalendar: any;
  public viewDate: Date = new Date();
  public isCalendarEnd = false;
  public isCalendarStart = false;
  testAndReviewDays = [];
  events: CalendarDayEvent[];

  isSortedByLastName: boolean | null = null; // `null` means no sort applied
  isSortedByLastActive: boolean | null = null; // `null` means no sort applied

  // Track the sorting direction
  sortAscendingLastName: boolean = true; // Sort direction for lastName (default: ascending)
  sortAscendingLastActive: boolean = true; // Sort direction for lastActive (default: ascending)
  childLoading = false;

  activeTeachers: any[];
  inactiveTeachers: any[];
  notification: any;

  constructor(
    private schoolService: SchoolService,
    private router: ActivatedRoute,
    private authService: AuthService,
    public route: Router,
    public storageService: StorageService,
    public analytics: AnalyticsService,
    public spinner: NgxSpinnerService,
    public subjectService: SubjectService,
    private calendarService: CalendarService,
    private utilService: UtilService,
    private notificationService: NotificationsService,
  ) {}

  async ngOnInit() {
    try {
      this.spinner.show();
      this.currentPage = this.utilService.pageNumber ? this.utilService.pageNumber : 1;

      const currentId = Number(this.router.snapshot.paramMap.get('id'));
      if (currentId) {
        this.user = await this.getUserById(currentId);
      } else {
        this.user = this.storageService.getItem('user');
      }
      // await this.getInvalidSubjects(this.teachers);
      // if (!this.isDashboard) this.spinner.show();

      await this.getData();

      this.storageService.setSelectedSchoolsToLocalStorage.subscribe(async (res) => {
        if (res) {
          // this.spinner.show();
          await this.getData();
          // this.spinner.hide();
        }
      });
      // if (!this.isDashboard) this.spinner.hide();

      await this.getAllTeachersRecords();

      // this.searchControl.valueChanges
      //   .pipe(debounceTime(300))
      //   .subscribe((value) => {
      //     this.searchTeacher(value);
      //   });
      this.searchControl.valueChanges
        .pipe(
          debounceTime(300), // Waits 300ms after the last keystroke
          switchMap((value) => this.searchTeacher(value)), // Cancels previous requests if a new value comes in
        )
        .subscribe();
      this.spinner.hide();
    } catch (error) {
      this.spinner.hide();
      // this.loading = false;
      // console.log(error);
    }
  }

  async getData() {
    await this.getAllTeachers();

    await this.getUsersPerSelectedSchool();
  }

  async getInvalidSubjects(teacherIds: number[]) {
    const body = {
      user_id: teacherIds,
      table_reference: 'credential-document',
    };
    return await this.notificationService.getAllSentNotificationsToUser(body);
  }

  async getAllTeachers() {
    const schoolId = this.getCurrentSchool();

    const queryParams = {
      school_id: schoolId,
      page: this.currentPage,
    };

    const res: any = await this.authService.getTeachersBySchoolId(queryParams);
    if (res) {
      this.totalPages = res.pageTotal;
      this.teachers = res.items;
    }
  }

  async filterTeachers(userId: number) {
    const isExist = this.notification.filter((x) => x.user_id === userId);
    return isExist[0]?.count > 0 ? true : false;
  }

  async getListByPage() {
    try {
      // this.loading = true;
      await this.getUsersPerSelectedSchool();
      this.resetSortIcons();
      // this.loading = false;
    } catch (error) {
      this.loading = false;
      // console.log(error);
    }
  }

  async getUsersPerSelectedSchool() {
    try {
      this.childLoading = true;
      const schoolId = this.getCurrentSchool();
      await this.getTeachersBySchoolId(schoolId);
      this.childLoading = false;
      await this.getCurrentCalendar();
    } catch (error) {
      // console.log(error);
    }
  }

  showTeacherDashboardLoad(teacherId: number): void {
    const teacher = this.teachers.find((t) => t.id === teacherId);
    if (teacher) {
      teacher.loading = true;
      // Simulate some asynchronous action
      setTimeout(() => {
        teacher.loading = false;
        // Add logic to show teacher dashboard
      }, 2000);
    }
  }

  getCurrentSchool() {
    const currentSchoolId = Number(this.router.snapshot.params.schoolId);

    if (currentSchoolId) {
      return currentSchoolId;
    }

    const localStorageSchool = this.storageService.getItem('selectedSchoolByAdmin');

    if (localStorageSchool) {
      return localStorageSchool.id;
    }

    return this.user.user.school_id;
  }

  async getUserById(id) {
    try {
      const user = await this.authService.userById(id);

      return {
        authToken: null,
        user: user,
      };
    } catch (error) {
      // console.log(error);
    }
  }

  async getTeachersBySchoolId(schoolId: number) {
    try {
      // this.spinner.show();
      this.currentSchoolId = schoolId;
      const body = {
        school_id: schoolId,
        page: this.currentPage,
      };

      if (this.isMock) {
        // If it's a trial user, use the predefined TEACHERS_LIST and set totalPages to 1
        this.totalPages = 1;
        this.teachers = TEACHERS_LIST;
      } else {
        const response: any = await this.authService.teachersBySchoolId(body);
        this.totalPages = response.pageTotal;
        this.teachers = response.items;

        const now = Date.now();
        const inactiveTeachers = [];

        this.teachers.forEach((teacher) => {
          const activityLog = teacher.last_user_activity_log_of_user?.created_at;

          if (!activityLog) return;

          const lastLoginTime = new Date(activityLog).getTime();
          const timeDifference = now - lastLoginTime;
          const checkActiveHour = 24 * 60 * 60 * 1000; // 24 hours

          if (timeDifference > checkActiveHour) {
            teacher.user_activity_status = 'logged_out';
            inactiveTeachers.push(teacher);
          }
        });

        if (inactiveTeachers.length > 0) {
          await this.utilService.updateTeacherLoginStatus({ teachers_data: inactiveTeachers });
        }

        await this.getSubjectForTeacherPerClassroom(this.teachers);

        await this.getAllPages();
        const teacherIds: number[] = this.teachers.map((teacher) => teacher.id);
        this.notification = await this.getInvalidSubjects(teacherIds);

        if (this.notification) {
          this.teachers = await Promise.all(
            this.teachers.map(async (item) => ({
              ...item,
              essa_status: await this.filterTeachers(item.id),
            })),
          );
        }
        // this.spinner.hide();
      }
    } catch (error) {
      this.spinner.hide();
      console.log('getTeacherById', error);
    }
  }

  async getAllSchools() {
    try {
      this.schools = await this.schoolService.getAllSchools();
    } catch (error) {
      // console.log(error);
    }
  }

  async getAllPages() {
    try {
      this.pages = [];
      for (let i = 1; i <= this.totalPages; i++) {
        this.pages.push(i);
      }
    } catch (error) {
      // console.log(error);
    }
  }

  // showTeacherDashboard(userId: number): void {
  //   try {
  //     this.authService.closeDialog.emit(true);
  //     this.isDashboard
  //       ? this.isExpand
  //         ? this.emitExpandViewOfAdminDashboard(userId)
  //         : this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
  //             state: { currentSchoolId: this.currentSchoolId },
  //           })
  //       : this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
  //           state: { currentSchoolId: this.currentSchoolId },
  //         });
  //     this.utilService.pageNumber = this.currentPage;

  //     localStorage.removeItem('currentClassroom');
  //     localStorage.removeItem('currentClassroomSubject');
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  // emitExpandViewOfAdminDashboard(userId: number) {
  //   try {
  //     this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
  //       state: { currentSchoolId: this.currentSchoolId },
  //     });
  //     this.utilService.pageNumber = this.currentPage;
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  /**
   * @description Builds the state object for the route navigation.
   * This method checks if the current user is a SUPER_ADMIN and, if so, includes the adminId in the state.
   * The state always includes the currentSchoolId.
   *
   * @returns {any} The state object containing currentSchoolId and possibly adminId.
   */
  private buildStateWithAdminId(): any {
    const user = this.storageService.getItem('user');
    const isSuperAdmin = this.storageService.checkUserRole(user.user.role_id, ROLE.SUPER_ADMIN);

    const state: any = {
      currentSchoolId: this.currentSchoolId,
    };

    if (isSuperAdmin) {
      state.adminId = this.user.user.id;
    }

    return state;
  }

  /**
   * @description Navigates to the teacher details page, handling both the dashboard and expanded view cases.
   * If the dashboard is active, the method checks whether the view should be expanded or not,
   * and either calls emitExpandViewOfAdminDashboard or performs a regular navigation.
   *
   * @param {number} userId - The ID of the user (teacher) whose details should be shown.
   *
   * @returns {void} This function does not return anything, it performs a navigation.
   */
  showTeacherDashboard(userId: number): void {
    try {
      // Close any open dialog
      this.authService.closeDialog.emit(true);

      if (this.isDashboard) {
        if (this.isExpand) {
          // If expanded view is requested, call the emitExpandViewOfAdminDashboard method
          this.emitExpandViewOfAdminDashboard(userId);
        } else {
          // Otherwise, navigate to the regular teacher details page with the appropriate state
          const state = this.buildStateWithAdminId();
          this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
            state,
          });
        }
      } else {
        this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
          state: { currentSchoolId: this.currentSchoolId },
        });
      }

      this.utilService.pageNumber = this.currentPage;

      localStorage.removeItem('currentClassroom');
      localStorage.removeItem('currentClassroomSubject');
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @description Navigates to the teacher details page when the expanded view is requested by the admin.
   * This method is used when the admin wants to view the teacher's details in an expanded format.
   * It includes the currentSchoolId and optionally the adminId if the user is a SUPER_ADMIN.
   *
   * @param {number} userId - The ID of the user (teacher) whose details should be shown.
   *
   * @returns {void} This function does not return anything, it performs a navigation.
   */
  emitExpandViewOfAdminDashboard(userId: number) {
    try {
      // Build the state with currentSchoolId and possibly adminId
      const state = this.buildStateWithAdminId();
      this.route.navigateByUrl(`/profile/teacher-details/${userId}/${this.isExpand}`, {
        state,
      });
      this.utilService.pageNumber = this.currentPage;
    } catch (error) {
      console.log(error);
    }
  }

  closeDialog() {
    try {
      this.authService.closeDialog.emit(true);
    } catch (error) {
      // console.log(error);
    }
  }

  async getAllTeachersRecords() {
    const localStorageSchools = this.storageService.getItem('selectedSchoolByAdmin');
    const queryParams = {
      school_id: localStorageSchools?.id ? localStorageSchools?.id : this.user?.user?.school_id,
    };
    const res: any = await this.authService.teachersBySchoolId(queryParams);
    // const res = this.teachers;
    if (res) {
      this.allTeachers = res;
      await this.getSubjectForTeacherPerClassroom(this.allTeachers);
    }
  }

  /**
   * @description Get all the subjects added by teachers for the specified classroom
   * @param teacherList
   * @returns void
   */
  async getSubjectForTeacherPerClassroom(teacherList: any): Promise<void> {
    for (const teacher of teacherList) {
      let count: number = 0;
      const subjects = await this.getSubjectByIds(teacher);
      teacher.teachingSubjectName = subjects;
      teacher.teachingSubjectName = subjects;
      // Made the classroom_list optional to avoid error
      if (teacher?.classroom_list?.length > 0) {
        for (const classroom of teacher.classroom_list) {
          if (classroom?.isManual) {
            count = classroom.total_added_manual_pacing_plans > 0 ? count + 1 : 0;
          } else {
            if (classroom.global_pacing_plan.some((subArray) => subArray.length > 0)) {
              count += 1;
            }
          }
        }
      }
      teacher.countClassWithPlans = count;
    }
  }

  async searchTeacher(value: string) {
    try {
      this.loading = true;
      if (value !== '') {
        this.isPaginate = false;
        this.teachers = this.allTeachers;
        await this.getCurrentCalendar();
      } else {
        this.isPaginate = true;
        // this.getTeachersBySchoolId(this.currentSchoolId);
        await this.getUsersPerSelectedSchool();
      }
      this.loading = false;
    } catch (error) {
      this.loading = false;
    }
  }

  // async getSubjectByIds(user) {
  //   try {
  //     const subject_ids = user.teachingInfo.teachingSubject;
  //     return subject_ids
  //       .map((id) => SUBJECT_DISPLAY_NAMES[id])
  //       .filter((subject) => subject !== undefined)
  //       .map((subject) => subject.toString())
  //       .join(', ');
  //   } catch (error) {
  //     console.error('Error getting subject by IDs:', error);
  //   }
  // }

  async getSubjectByIds(user) {
    try {
      // Made the subject ids optional chaining to avoid error if subject ids is undefined
      const subject_ids = user?.teachingInfo?.teachingSubject;
      if (subject_ids && subject_ids.length > 0) {
        return subject_ids
          .map((id) => {
            return SUBJECT_DISPLAY_NAMES[id];
          })
          .filter((subject) => subject !== undefined)
          .map((subject) => subject.toString())
          .join(', ');
      } else return null;
    } catch (error) {
      console.error('Error getting subject by IDs:', error);
    }
  }

  /**
   * @description Check if the user is active or not based on the last active time of the user.
   * @param lastActiveTime
   * @returns boolean
   */
  isUserActive(lastActiveTime: string | Date): boolean {
    if (!lastActiveTime) {
      return false;
    }
    // Convert the provided time to a moment object
    const lastActiveDate = moment(lastActiveTime);
    const currentTime = moment();

    // Check if the time difference is within 24 hours
    return currentTime.diff(lastActiveDate, 'hours') <= 24;
  }

  private async getSchoolCalendarBySchoolId() {
    try {
      const params = {
        school_id: this.user.user.school_id ? this.user.user.school_id : this.currentSchoolId,
      };
      return await this.calendarService.schoolEventBySchoolId(params);
    } catch (error) {
      // console.log(error);
    }
  }

  async getCurrentCalendar(isAddEvent = false) {
    try {
      this.currentCalendar = (await this.getSchoolCalendarBySchoolId()) as any;

      if (this.user.user.is_trial_user) {
        this.currentCalendar[0].start = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

        this.setEventsAsCurrentYearEvents();
      } else {
        if (!isAddEvent) this.viewDate = new Date(this.currentCalendar[0].start);
      }
      this.events = [];

      await this.getTestAndReviewDays();
      await this.getDates(this.teachers);
      this.events = await this.updateEventsData();
    } catch (error) {
      console.log('getCurrentCalendar', error);
    }
  }

  public checkNavigatePreviousState(): void {
    try {
      const [calStart] = this.currentCalendar;
      if (calStart) {
        const { start, end } = calStart;
        const currentCalendarStartDate = new Date(start);
        const currentCalendarEndDate = new Date(end);

        const currentMonthYear = `${this.viewDate.getMonth()},${this.viewDate.getFullYear()}`;
        this.isCalendarStart =
          currentCalendarStartDate.getMonth() + ',' + currentCalendarStartDate.getFullYear() === currentMonthYear;
        this.isCalendarEnd =
          currentCalendarEndDate.getMonth() + ',' + currentCalendarEndDate.getFullYear() === currentMonthYear;
      } else {
        this.isCalendarStart = false;
        this.isCalendarEnd = false;
      }
    } catch (error) {
      // console.log(error);
    }
  }

  async updateEventsData() {
    try {
      const events = [];
      this.currentCalendar[0].holidays.map((event) => {
        return events.push({
          start: new Date(event.start),
          end: new Date(event.end),
          title: event.title,
          cssClass: HOLIDAY_EVENT[0].cssClass,
        });
      }),
        this.currentCalendar[0].pupil_free.map((event) =>
          events.push({
            start: new Date(event.start),
            end: new Date(event.end),
            title: event.title,
            cssClass: PUPIL_EVENT[0].cssClass,
          }),
        ),
        this.currentCalendar[0].acceleration.map((event) =>
          events.push({
            start: new Date(event.start),
            end: new Date(event.end),
            title: event.title,
            cssClass: ACCELERATION_EVENT[0].cssClass,
          }),
        );

      this.currentCalendar[0].unassigned.map((event) =>
        events.push({
          start: new Date(event.start),
          end: new Date(event.end),
          title: event.title,
          cssClass: UNASSIGNED_EVENT[0].cssClass,
        }),
      );

      return events;
    } catch (error) {
      // console.log(error);
    }
  }

  private setEventsAsCurrentYearEvents(): void {
    try {
      this.currentCalendar[0].holidays.map((x) => {
        x.end = new Date(x.end).setFullYear(new Date().getFullYear());
        x.start = new Date(x.start).setFullYear(new Date().getFullYear());
      });

      this.currentCalendar[0].acceleration.map((x) => {
        x.end = new Date(x.end).setFullYear(new Date().getFullYear());
        x.start = new Date(x.start).setFullYear(new Date().getFullYear());
      });

      this.currentCalendar[0].pupil_free.map((x) => {
        x.end = new Date(x.end).setFullYear(new Date().getFullYear());
        x.start = new Date(x.start).setFullYear(new Date().getFullYear());
      });

      this.currentCalendar[0].bank_days.map((x) => {
        x.end = new Date(x.end).setFullYear(new Date().getFullYear());
        x.start = new Date(x.start).setFullYear(new Date().getFullYear());
      });

      this.currentCalendar[0].unassigned.map((x) => {
        x.end = new Date(x.end).setFullYear(new Date().getFullYear());
        x.start = new Date(x.start).setFullYear(new Date().getFullYear());
      });
    } catch (error) {}
  }

  ifDayIsTestOrReviewDay(date: Date) {
    try {
      if (this.testAndReviewDays.length > 0) {
        return this.testAndReviewDays.filter(
          (event) => new Date(event.start.setHours(0, 0, 0, 0)).getTime() === date.getTime(),
        );
      } else {
        return [];
      }
    } catch (error) {
      // console.log(error);
    }
  }

  async getTestAndReviewDays() {
    try {
      const date1 = dayjs(this.currentCalendar[0].start);
      const date2 = dayjs(this.currentCalendar[0].end);
      const diff = date2.diff(date1, 'day');
      const dates = [];

      for (let i = 0; i < diff; i++) {
        const startDate: any = dayjs(new Date(this.currentCalendar[0].start))
          .year(new Date(this.currentCalendar[0].start).getFullYear())
          .add(i, 'day');
        const date: any = {
          start: dayjs(startDate, 'YYYY-MM-DD').toDate(),
          end: dayjs(startDate.endOf('day'), 'YYYY-MM-DD').toDate(),
          isDay:
            startDate.day() === 0 || startDate.day() === 6
              ? false
              : !this.events.find((event) => {
                  if (
                    startDate.startOf('day').valueOf() >= new Date(event.start).getTime() &&
                    startDate.startOf('day').valueOf() <= new Date(event.end).getTime()
                  ) {
                    return event;
                  }
                }),
        };
        date.isDay ? dates.push(date) : null;
      }
    } catch (error) {
      console.log('getTestAndReviewDays', error);
    }
  }

  async getDates(teachersList: any) {
    try {
      this.dates = [];
      const dates = [];
      const date1 = dayjs(this.currentCalendar[0].start);
      const date2 = dayjs(this.currentCalendar[0].end);
      const diff = date2.diff(date1, 'day');
      const calendarStartDate =
        this.user.role_id === 19
          ? dayjs().startOf('year').add(1, 'day')
          : this.user.is_trial_user
            ? dayjs(this.currentCalendar[0].start)
            : dayjs(this.currentCalendar[0].start);
      for (let i = 0; i < diff; i++) {
        const startDate: any = dayjs(calendarStartDate).add(i, 'day');
        const ifCurrentDateIsTestAndReviewDay = this.ifDayIsTestOrReviewDay(
          new Date(startDate.$d.setHours(0, 0, 0, 0)),
        );
        const date: any = {
          start: dayjs(startDate.startOf('day'), 'YYYY-MM-DD').toDate(),
          end: dayjs(startDate.endOf('day'), 'YYYY-MM-DD').toDate(),
          addEvent:
            startDate.day() === 0 || startDate.day() === 6 || ifCurrentDateIsTestAndReviewDay.length > 0
              ? false
              : this.checkIfDateIsEventDate(startDate?.$d)
                ? false
                : true,
        };
        this.dates.push(date);
      }
      teachersList.forEach((teacher) => {
        // Made the classroom_list optional to avoid error
        teacher?.classroom_list?.forEach(async (classroom) => {
          if (classroom.isManual !== true) {
            this.testAndReviewDays = [];
            const testDay = classroom?.test_day ? classroom?.test_day : this.user.user.teachingInfo?.examPerdays;
            let newIndex = testDay;

            dates.map((event) => {
              const testDayValues = dates[newIndex];

              const reviewDayValues = dates[newIndex + 1];

              if (testDayValues) {
                //   // this.filterTestAndReviewDayData(this.deletedTestDays);

                this.testAndReviewDays.push({
                  start: testDayValues.start,
                  end: testDayValues.end,
                  isDay: testDayValues.isDay,
                  type: 'Test Day',
                });
              }

              if (reviewDayValues) {
                //   // this.filterTestAndReviewDayData(this.deletedReviewDays);
                this.testAndReviewDays.push({
                  start: reviewDayValues.start,
                  end: reviewDayValues.end,
                  isDay: reviewDayValues.isDay,
                  type: 'Review Day',
                });
              }
              newIndex = newIndex + (testDay + 2);
              // console.log('798 this.dates ', this.dates);

              return event;
            });
            const filteredDates = this.dates.filter((x) => x.addEvent === true);
            if (classroom.global_pacing_plan.length > 0) {
              classroom.global_pacing_plan.forEach((element) => {
                // element = [element];
                element.map((x, i) => {
                  // Made the start optional to avoid error
                  const data1 = this.ifDayIsTestOrReviewDay(new Date(filteredDates[i]?.start));
                  if (filteredDates[i] && data1.length === 0) {
                    x.start = new Date(filteredDates[i].start);
                    x.end = new Date(filteredDates[i].end);
                    return x;
                  }
                });

                const data = element.filter((x) => x.start);
                classroom['auto_populate_global_pacing_plan_list'] = data.length;
                const data1 = data.filter((x) => x.start < new Date());
                classroom['auto_populate_global_pacing_plan_list_till_date'] = data1.length;
              });
            }
          }
          teacher['auto_populate_global_pacing_plan_list_till_date'] = teacher.classroom_list.reduce(
            (sum, item) =>
              sum +
              (item.auto_populate_global_pacing_plan_list_till_date
                ? item.auto_populate_global_pacing_plan_list_till_date
                : 0),
            0,
          );
        });
      });
    } catch (error) {
      console.log('getDates', error);
    }
  }

  checkIfDateIsEventDate(date: Date) {
    try {
      return this.events.some(
        (x) =>
          date.setHours(0, 0, 0, 0) >= new Date(x.start).setHours(0, 0, 0, 0) &&
          date.getTime() <= new Date(x.end).setHours(23, 59, 59, 59),
      );
    } catch (error) {
      // console.log(error);
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case UserActivityStatus.ACTIVE:
        return 'status-green';
      case UserActivityStatus.INACTIVE:
        return 'status-yellow';
      case UserActivityStatus.LOGGED_OUT:
        return 'status-red';
      default:
        return '';
    }
  }

  getStatusImagePath(status: string): string {
    switch (status) {
      case UserActivityStatus.ACTIVE:
        return '/assets/icons/green.png';
      case UserActivityStatus.INACTIVE:
        return '/assets/icons/yellow.png';
      case UserActivityStatus.LOGGED_OUT:
        return '/assets/icons/red.png';
      default:
        return '';
    }
  }

  sortBy(field: string, direction: boolean) {
    // Sorting logic for 'lastName'
    if (field === 'lastName') {
      if (this.isSortedByLastName === null) {
        this.sortAscendingLastName = direction;
        this.isSortedByLastName = true;
      } else {
        this.sortAscendingLastName = !this.sortAscendingLastName;
      }
      this.isSortedByLastActive = null; // Reset sort state for other columns

      this.teachers.sort((a, b) => {
        const valueA = a[field].toLowerCase();
        const valueB = b[field].toLowerCase();
        return this.sortAscendingLastName ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
    } else if (field === 'lastactive') {
      if (this.isSortedByLastActive === null) {
        this.sortAscendingLastActive = direction;
        this.isSortedByLastActive = true;
      } else {
        this.sortAscendingLastActive = !this.sortAscendingLastActive;
      }
      this.isSortedByLastName = null; // Reset sort state for other columns

      this.teachers.sort((a, b) => {
        const logA = a.last_user_activity_log_of_user;
        const logB = b.last_user_activity_log_of_user;

        // Helper function to get the comparison date from each log, returning a default future date if missing
        const getDate = (log: any) => {
          return log ? new Date(log.expired_time || log.created_at) : new Date(8640000000000000); // Very large date to push nulls to the end
        };

        const dateA = getDate(logA);
        const dateB = getDate(logB);

        // Perform the actual comparison based on the direction of sorting
        if (dateA < dateB) {
          return this.sortAscendingLastActive ? -1 : 1;
        } else if (dateA > dateB) {
          return this.sortAscendingLastActive ? 1 : -1;
        } else {
          return 0;
        }
      });
    }
  }

  // Function to reset sort icons
  resetSortIcons() {
    this.isSortedByLastName = null;
    this.isSortedByLastActive = null;
    this.sortAscendingLastName = true;
    this.sortAscendingLastActive = true;
  }
}
